import React, { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GoBack from "../components/GoBack";
import {
  getApplicationInfo,
  putApplicationInfo,
  ApplicationInfoDto,
} from "../api/services";
import { AnimalType } from "../types";

const BasicInformation: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [animalOption, setAnimalOption] = useState<AnimalType | "">("");
  const [ssn, setSsn] = useState("");
  const [rentersInsurance, setRentersInsurance] = useState<boolean | null>(
    null
  );
  const [rentersInsuranceInterested, setRentersInsuranceInterested] = useState<
    boolean | null
  >(null);
  const [ssnFocused, setSsnFocused] = useState(false);

  const [usResident, setUsResident] = useState<boolean | null>(null);
  const [bestDescription, setBestDescription] = useState("");
  const [nonUsBestDescription, setNonUsBestDescription] = useState("");
  const [annualIncome, setAnnualIncome] = useState("");
  const [monthlyRent, setMonthlyRent] = useState("");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const maskSsn = (full: string) => {
    if (!full) return "";
    const last4 = full.slice(-4);
    return last4.length === 4 ? "*****" + last4 : full;
  };

  const formatCurrency = (value: string): string => {
    if (!value) return "";
    const number = parseFloat(value.replace(/[^0-9.]/g, ""));
    if (isNaN(number)) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(number);
  };

  const parseCurrency = (value: string): number => {
    const number = parseFloat(value.replace(/[^0-9.]/g, ""));
    return isNaN(number) ? 0 : number;
  };

  useEffect(() => {
    if (!id) return;
    getApplicationInfo(id)
      .then((res) => {
        const info: ApplicationInfoDto = res.data;
        setSsn(info.ssn ?? "");
        setRentersInsurance(
          typeof info.rentersInsurance === "boolean"
            ? info.rentersInsurance
            : null
        );
        setRentersInsuranceInterested(
          typeof info.rentersInsuranceInterested === "boolean"
            ? info.rentersInsuranceInterested
            : null
        );
        setAnimalOption(info.animal ?? "");
        setUsResident(
          typeof info.usResident === "boolean" ? info.usResident : null
        );
        setBestDescription(info.bestDescription ?? "");
        setNonUsBestDescription(info.nonUsBestDescription ?? "");
        setAnnualIncome(info.annualIncome ? info.annualIncome.toString() : "");
        setMonthlyRent(info.monthlyRent ? info.monthlyRent.toString() : "");
      })
      .catch((err) => {
        console.error(err);
        setError(t("basicInformation.errorFetch"));
      })
      .finally(() => setLoading(false));
  }, [id, t]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setError(null);
    setSuccessMsg(null);

    const payload: ApplicationInfoDto = {
      ssn: ssn || "",
      rentersInsurance: !!rentersInsurance,
      rentersInsuranceInterested: !!rentersInsuranceInterested,
      animal: animalOption as AnimalType,
      usResident: usResident ?? undefined,
      bestDescription: bestDescription || undefined,
      nonUsBestDescription: nonUsBestDescription || undefined,
      annualIncome: annualIncome ? parseCurrency(annualIncome) : undefined,
      monthlyRent: monthlyRent ? parseCurrency(monthlyRent) : undefined,
    };

    putApplicationInfo(id, payload)
      .then(() => {
        setSuccessMsg(t("basicInformation.savedSuccess"));
        setTimeout(() => {
          navigate(`/application/${id}`);
        }, 1500);
      })
      .catch((err) => {
        console.error(err);
        setError(t("basicInformation.errorSave"));
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return <p>{t("basicInformation.loading")}</p>;
  }

  return (
    <div>
      <GoBack />
      <h2 className="mb-6">{t("basicInformation.title")}</h2>

      {error && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{error}</div>}
      {successMsg && <div className="rounded-lg bg-green-50 px-4 py-3 text-sm text-success">{successMsg}</div>}

      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="p-5">
          <form onSubmit={handleSubmit} className="max-w-[500px]">
            <fieldset className="mb-4">
              <legend className="mb-2 block text-sm font-medium text-gray-700">
                {t("basicInformation.usResidentLegend")}{" "}
                <span className="text-danger">*</span>
              </legend>
              <div className="flex items-center gap-2">
                <input
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                  type="radio"
                  name="usResident"
                  id="usResidentYes"
                  value="yes"
                  checked={usResident === true}
                  onChange={() => setUsResident(true)}
                  required
                />
                <label className="text-sm text-gray-700" htmlFor="usResidentYes">
                  {t("basicInformation.yes")}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                  type="radio"
                  name="usResident"
                  id="usResidentNo"
                  value="no"
                  checked={usResident === false}
                  onChange={() => setUsResident(false)}
                />
                <label className="text-sm text-gray-700" htmlFor="usResidentNo">
                  {t("basicInformation.no")}
                </label>
              </div>
            </fieldset>

            {usResident === true && (
              <div className="mb-4">
                <label htmlFor="bestDescription" className="mb-1.5 block text-sm font-medium text-gray-700">
                  {t("basicInformation.bestDescriptionLabel")}{" "}
                  <span className="text-danger">*</span>
                </label>
                <select
                  id="bestDescription"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                  value={bestDescription}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                    setBestDescription(e.target.value)
                  }
                  required
                >
                  <option value="">{t("basicInformation.selectOption")}</option>
                  <option value="self-employed">
                    {t("basicInformation.bestDescriptions.selfEmployed")}
                  </option>
                  <option value="student-retired">
                    {t("basicInformation.bestDescriptions.studentRetired")}
                  </option>
                  <option value="relocating">
                    {t("basicInformation.bestDescriptions.relocating")}
                  </option>
                  <option value="other">
                    {t("basicInformation.bestDescriptions.other")}
                  </option>
                </select>
              </div>
            )}

            {usResident === false && (
              <>
                <div className="mb-4">
                  <label htmlFor="nonUsBestDescription" className="mb-1.5 block text-sm font-medium text-gray-700">
                    {t("basicInformation.nonUsBestDescriptionLabel")}{" "}
                    <span className="text-danger">*</span>
                  </label>
                  <select
                    id="nonUsBestDescription"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    value={nonUsBestDescription}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setNonUsBestDescription(e.target.value)
                    }
                    required
                  >
                    <option value="">
                      {t("basicInformation.selectOption")}
                    </option>
                    <option value="currently-employed">
                      {t(
                        "basicInformation.nonUsDescriptions.currentlyEmployed"
                      )}
                    </option>
                    <option value="student">
                      {t("basicInformation.nonUsDescriptions.student")}
                    </option>
                    <option value="other">
                      {t("basicInformation.nonUsDescriptions.other")}
                    </option>
                  </select>
                </div>

                {nonUsBestDescription === "currently-employed" && (
                  <>
                    <div className="mb-4">
                      <label htmlFor="annualIncome" className="mb-1.5 block text-sm font-medium text-gray-700">
                        {t("basicInformation.annualIncomeLabel")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="annualIncome"
                        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                        value={annualIncome ? formatCurrency(annualIncome) : ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setAnnualIncome(
                            e.target.value.replace(/[^0-9.]/g, "")
                          )
                        }
                        placeholder={t("basicInformation.currencyPlaceholder")}
                        required
                      />
                    </div>
                    <div className="mb-4">
                      <label htmlFor="monthlyRent" className="mb-1.5 block text-sm font-medium text-gray-700">
                        {t("basicInformation.monthlyRentLabel")}{" "}
                        <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        id="monthlyRent"
                        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                        value={monthlyRent ? formatCurrency(monthlyRent) : ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          setMonthlyRent(e.target.value.replace(/[^0-9.]/g, ""))
                        }
                        placeholder={t("basicInformation.currencyPlaceholder")}
                        required
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <div className="mb-4">
              <label htmlFor="animalOption" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("basicInformation.petsLabel")}{" "}
                <span className="text-danger">*</span>
              </label>
              <select
                id="animalOption"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                value={animalOption}
                onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                  setAnimalOption(e.target.value as AnimalType)
                }
                required
              >
                <option value="">{t("basicInformation.selectOption")}</option>
                <option value={AnimalType.Pet}>
                  {t("basicInformation.options.pet")}
                </option>
                <option value={AnimalType.ServiceAnimal}>
                  {t("basicInformation.options.serviceAnimal")}
                </option>
                <option value={AnimalType.Both}>
                  {t("basicInformation.options.both")}
                </option>
                <option value={AnimalType.Neither}>
                  {t("basicInformation.options.neither")}
                </option>
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="ssn" className="mb-1.5 block text-sm font-medium text-gray-700">
                {t("basicInformation.ssnLabel")}
              </label>
              <input
                type="text"
                id="ssn"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                value={ssnFocused ? ssn : maskSsn(ssn)}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setSsn(e.target.value)
                }
                onFocus={() => setSsnFocused(true)}
                onBlur={() => setSsnFocused(false)}
              />
            </div>

            <fieldset className="mb-4">
              <legend className="mb-2 block text-sm font-medium text-gray-700">
                {t("basicInformation.rentersInsuranceLegend")}{" "}
                <span className="text-danger">*</span>
              </legend>
              <div className="flex items-center gap-2">
                <input
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                  type="radio"
                  name="rentersInsurance"
                  id="insuranceYes"
                  value="yes"
                  checked={rentersInsurance === true}
                  onChange={() => setRentersInsurance(true)}
                  required
                />
                <label className="text-sm text-gray-700" htmlFor="insuranceYes">
                  {t("basicInformation.yes")}
                </label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                  type="radio"
                  name="rentersInsurance"
                  id="insuranceNo"
                  value="no"
                  checked={rentersInsurance === false}
                  onChange={() => setRentersInsurance(false)}
                />
                <label className="text-sm text-gray-700" htmlFor="insuranceNo">
                  {t("basicInformation.no")}
                </label>
              </div>
            </fieldset>

            {rentersInsurance === false && (
              <fieldset className="mb-4">
                <legend className="mb-2 block text-sm font-medium text-gray-700">
                  {t("basicInformation.quoteInfoLegend")}{" "}
                  <span className="text-danger">*</span>
                </legend>
                <div className="flex items-center gap-2">
                  <input
                    className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                    type="radio"
                    name="rentersInsuranceInterested"
                    id="infoYes"
                    value="yes"
                    checked={rentersInsuranceInterested === true}
                    onChange={() => setRentersInsuranceInterested(true)}
                    required
                  />
                  <label className="text-sm text-gray-700" htmlFor="infoYes">
                    {t("basicInformation.yes")}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    className="h-4 w-4 border-gray-300 text-primary focus:ring-primary/20"
                    type="radio"
                    name="rentersInsuranceInterested"
                    id="infoNo"
                    value="no"
                    checked={rentersInsuranceInterested === false}
                    onChange={() => setRentersInsuranceInterested(false)}
                  />
                  <label className="text-sm text-gray-700" htmlFor="infoNo">
                    {t("basicInformation.no")}
                  </label>
                </div>
              </fieldset>
            )}

            <button type="submit" className="rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-light" disabled={saving}>
              {saving
                ? t("basicInformation.saving")
                : t("basicInformation.buttons.submit")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BasicInformation;
